import html2pdf from 'html2pdf.js';

export interface PdfOptions {
  filename?: string;
  margin?: number | [number, number, number, number];
  pageSize?: 'a4' | 'letter' | 'legal';
  orientation?: 'portrait' | 'landscape';
  imageQuality?: number;
}

const defaultOptions: PdfOptions = {
  filename: 'document.pdf',
  margin: 10,
  pageSize: 'a4',
  orientation: 'portrait',
  imageQuality: 0.98,
};

/**
 * Generate a PDF from HTML content and trigger download
 */
export async function generatePdfFromHtml(
  htmlContent: string,
  options: PdfOptions = {}
): Promise<void> {
  const mergedOptions = { ...defaultOptions, ...options };

  // Create a temporary container for the HTML
  const container = document.createElement('div');
  container.innerHTML = htmlContent;
  container.style.position = 'absolute';
  container.style.left = '-9999px';
  container.style.top = '0';
  document.body.appendChild(container);

  const html2pdfOptions = {
    margin: mergedOptions.margin,
    filename: mergedOptions.filename,
    image: { type: 'jpeg', quality: mergedOptions.imageQuality },
    html2canvas: { 
      scale: 2, 
      useCORS: true,
      logging: false,
    },
    jsPDF: { 
      unit: 'mm', 
      format: mergedOptions.pageSize, 
      orientation: mergedOptions.orientation 
    },
  };

  try {
    await html2pdf().set(html2pdfOptions).from(container).save();
  } finally {
    document.body.removeChild(container);
  }
}

/**
 * Generate a PDF blob from HTML content (for upload/storage)
 */
export async function generatePdfBlobFromHtml(
  htmlContent: string,
  options: PdfOptions = {}
): Promise<Blob> {
  const mergedOptions = { ...defaultOptions, ...options };

  const container = document.createElement('div');
  container.innerHTML = htmlContent;
  container.style.position = 'absolute';
  container.style.left = '-9999px';
  container.style.top = '0';
  document.body.appendChild(container);

  const html2pdfOptions = {
    margin: mergedOptions.margin,
    filename: mergedOptions.filename,
    image: { type: 'jpeg', quality: mergedOptions.imageQuality },
    html2canvas: { 
      scale: 2, 
      useCORS: true,
      logging: false,
    },
    jsPDF: { 
      unit: 'mm', 
      format: mergedOptions.pageSize, 
      orientation: mergedOptions.orientation 
    },
  };

  try {
    const blob = await html2pdf().set(html2pdfOptions).from(container).outputPdf('blob');
    return blob;
  } finally {
    document.body.removeChild(container);
  }
}
